import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PencilLine } from 'lucide-react';

import { GithubIcon, GoogleCalendarIcon, SlackIcon } from '@app/components/core/philBot/appIcons';
import { AiSummaryInstructions, DraftSources, ProductEntity } from '@data';
import { usePhillConnections } from '@data/hooks/auth';

export interface ReadingSource {
  key: string;
  label: string;
  detail: string;
  hover?: string;
  Icon: React.ComponentType<{ className?: string }>;
  /** The person's switch for this source, where it has one. */
  setting?: keyof DraftSources;
  /** Switched on, and with something to read. */
  reads: boolean;
}

/** At most this many names before the rest become a count. */
const MOST_SHOWN = 3;

/** Every connected source with its switch, the person's own note last; drafting reads the rows with `reads`. */
export const useReadingSources = (
  products: ProductEntity[],
  selected: string[],
  settings?: AiSummaryInstructions,
): ReadingSource[] => {
  const { t } = useTranslation();
  const { data: connections } = usePhillConnections();

  return useMemo(() => {
    const rows: ReadingSource[] = [];
    const on = (setting: keyof DraftSources): boolean => settings?.[setting] !== false;
    const shortList = (names: string[]): string => {
      const rest = names.length - MOST_SHOWN;
      const shown = names.slice(0, MOST_SHOWN).join(', ');

      return rest > 0 ? `${shown} ${t('phillReport.andMore', { count: rest })}` : shown;
    };

    // Only GitHub is named concretely: Slack's search has no channel filter, so naming channels would describe a query that is not run.
    const repositories = [
      ...new Set(
        products.filter(product => selected.includes(product.name)).flatMap(product => product.repositories ?? []),
      ),
    ];
    if (connections?.github) {
      rows.push({
        key: 'github',
        Icon: GithubIcon,
        label: t('phillReport.sourceGithub'),
        detail: repositories.length > 0 ? shortList(repositories) : t('phillReport.readingGithub'),
        setting: 'draftReadsGithub',
        // Drafting needs a repository to read; without one the row would name a source that adds nothing.
        reads: on('draftReadsGithub') && repositories.length > 0,
      });
    }
    if (connections?.slack) {
      rows.push({
        key: 'slack',
        Icon: SlackIcon,
        label: t('phillReport.sourceSlack'),
        detail: t('phillReport.readingSlack', { products: shortList(selected) }),
        setting: 'draftReadsSlack',
        reads: on('draftReadsSlack'),
      });
    }
    if (connections?.googleCalendar) {
      rows.push({
        key: 'calendar',
        Icon: GoogleCalendarIcon,
        label: t('phillReport.sourceCalendar'),
        detail: t('phillReport.readingCalendar'),
        setting: 'draftReadsCalendar',
        reads: on('draftReadsCalendar'),
      });
    }

    const note = settings?.aiSummaryInstructions?.trim();
    if (note) {
      rows.push({
        key: 'instructions',
        Icon: PencilLine,
        label: t('phillReport.sourceInstructions'),
        detail: t('phillReport.readingInstructions'),
        hover: note,
        reads: true,
      });
    }

    return rows;
  }, [connections, settings, products, selected, t]);
};
